import { defineStore } from 'pinia'
import { computed, ref, watchEffect } from 'vue'
import { StorageSerializers, useStorage } from '@vueuse/core'
import router from '@/router'

import {
  userAutoLogin,
  userLogin,
  type LoginParams,
  type AutoLoginParams,
  signPolicy,
  type SignPolicyParams,
  userHasTyCPPSigned,
} from '@/services/auth.services'
import { createOrganizationAdapter } from '../adapters'

export const useAuthStore = defineStore('auth', () => {
  const isAuthenticated = ref(false)
  const user = useStorage('user', null, localStorage, { serializer: StorageSerializers.object })
  const token = useStorage<string | null>('token', null)
  const dismissedPasswordChangeDialog = useStorage('dismissedPasswordChangeDialog', false)
  const needsPolicyAcceptance = ref(false)
  const hasAcceptedPolicy = ref(false)
  const INSURANCE = 'insurance'

  const organization = computed(() => {
    if (!user.value) return null

    return createOrganizationAdapter(user.value.organization)
  })

  const paymentMethod = computed(() => {
    if (!user.value) return null

    return user.value.payment_method
  })

  const isAnInsurance = computed(() => {
    return organization.value?.extraData.some((item) => item === INSURANCE)
  })

  watchEffect(() => {
    if (token.value && user.value) {
      isAuthenticated.value = true
    }
  })

  async function login(params: LoginParams) {
    console.log('login')
    try {
      const { data } = await userLogin(params)
      user.value = data
      token.value = data.token

      const { data: policyData } = await userHasTyCPPSigned(data.id)

      if (user.value) {
        const docs = policyData.documents
        user.value.all_signed = policyData.all_signed
        user.value.already_signed = docs.some((d: any) => d.already_signed)
        user.value.has_signed_tyc = docs.find((d: any) => d.code === 'TYC')?.signed ?? false
        user.value.has_signed_pp = docs.find((d: any) => d.code === 'PP')?.signed ?? false
      }

      if (policyData.all_signed !== true) {
        needsPolicyAcceptance.value = true
        return
      }

      router.push('/')
    } catch (error) {
      console.error('Error during login:', error)
      throw error
    }
  }

  async function acceptUserPolicy(params: SignPolicyParams) {
    await signPolicy(params)
    if (user.value) {
      user.value.all_signed = true
    }
    needsPolicyAcceptance.value = false
    hasAcceptedPolicy.value = true
    router.push('/')
  }

  async function autologin(params: AutoLoginParams) {
    try {
      const { data } = await userAutoLogin(params)
      user.value = data.data
      if (user.value) user.value.use_autologin = data.use_autologin
      token.value = data.data.token

      const { data: policyData } = await userHasTyCPPSigned(data.data.id)

      if (user.value) {
        const docs = policyData.documents
        user.value.all_signed = policyData.all_signed
        user.value.already_signed = docs.some((d: any) => d.already_signed)
        user.value.has_signed_tyc = docs.find((d: any) => d.code === 'TYC')?.signed ?? false
        user.value.has_signed_pp = docs.find((d: any) => d.code === 'PP')?.signed ?? false
      }

      if (policyData.all_signed !== true) {
        needsPolicyAcceptance.value = true
      }

      return data
    } catch (error: any) {
      console.log(error)
      throw error
    }

    // TODO: Hacer petición a ws de validación de usuario
    // const { data } = await userAutoLogin(params)
    // return new Promise((resolve, reject) => {
    //   if (Math.random() < 0.5) {
    // TODO: Si la petición es exitosa, se debe guardar el usuario en el localStorage y redirigir a la página de inicio o a donde pidan
    //     console.log('data autologin', data)
    //     console.log('params', params)
    //     user.value = data
    //     token.value = data.token
    //     setTimeout(() => {
    //       resolve(`autologin success ${params.key}`)
    //       console.log('exito', params.key)
    //     }, 1000)
    //   } else {
    //     reject('autologin error')
    //   }
    // })
  }

  return {
    isAuthenticated,
    user,
    token,
    hasAcceptedPolicy,
    organization,
    isAnInsurance,
    needsPolicyAcceptance,
    dismissedPasswordChangeDialog,
    paymentMethod,
    login,
    autologin,
    acceptUserPolicy,
  }
})
